import { Box } from "@mui/material";
import { Contact } from "@repo/server-api";
import { SalaryInformationType } from "@repo/server-api/types";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from "react";

import DateField from "../fields/DateField";
import EuroTextField from "../fields/EuroTextField";

const SalaryInformation = forwardRef(
	(
		props: Readonly<{
			contact?: Contact | null;
			setContact?: (value: Contact | null) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const startDateRef = useRef<{
			validate: () => Promise<Record<string, string>>;
		} | null>(null);
		const pricePerHourRef = useRef<{
			validate: () => Promise<Record<string, string>>;
		} | null>(null);

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};

			localErrors.startDate = (await startDateRef.current?.validate()) || {};
			localErrors.pricePerHour = (await pricePerHourRef.current?.validate()) || {};

			props.setErrors?.(localErrors);

			return localErrors;
		}, [props]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const info = useMemo(() => {
			const salaryInfo = props.contact?.additionalInformation.filter(
				(i): i is SalaryInformationType =>
					(i as SalaryInformationType).__typename === "SalaryInformation",
			)[0];
			return (
				salaryInfo ?? {
					id: "",
					__typename: "SalaryInformation",
					pricePerHour: 0,
					startDate: null,
				}
			);
		}, [props.contact]);

		return (
			<div>
				<Box className="fullCenter">
					<DateField
						isLoaded={true}
						ref={startDateRef}
						value={info.startDate ?? ""}
						setValue={(value) => {
							if (value === "Invalid Date") return;
							const additionalInformation = props.contact?.additionalInformation.filter(
								(i) => (i as SalaryInformationType).__typename !== "SalaryInformation",
							);
							props.setContact?.({
								...props.contact,
								additionalInformation: [
									...(additionalInformation ?? []),
									{
										id: info.id,
										__typename: "SalaryInformation",
										pricePerHour: info.pricePerHour,
										startDate: value ? dayjs(value).format("YYYY-MM-DD") : null,
									} as SalaryInformationType,
								],
							} as Contact);
						}}
						label={t("valid-from-date")}
						errors={props.errors?.startDate as Record<string, string>}
						setErrors={(value) =>
							props.setErrors && props.setErrors({ ...props.errors, startDate: value })
						}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mt: 3 }}>
					<Box sx={{ width: "100%" }}>
						<EuroTextField
							isLoaded={true}
							ref={pricePerHourRef}
							value={info.pricePerHour}
							setValue={(value) => {
								const additionalInformation = props.contact?.additionalInformation.filter(
									(i) => (i as SalaryInformationType).__typename !== "SalaryInformation",
								);
								props.setContact?.({
									...props.contact,
									additionalInformation: [
										...(additionalInformation ?? []),
										{
											id: info.id,
											__typename: "SalaryInformation",
											pricePerHour: value ?? 0,
											startDate: info.startDate,
										} as SalaryInformationType,
									],
								} as Contact);
							}}
							label={t("price-per-hour")}
							errors={props.errors?.pricePerHour as Record<string, string>}
							setErrors={(value) =>
								props.setErrors && props.setErrors({ ...props.errors, pricePerHour: value })
							}
							iconBackground="#1a455c"
						/>
					</Box>
				</Box>
			</div>
		);
	},
);

SalaryInformation.displayName = "SalaryInformation";
export default SalaryInformation;
